import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import FileUpload from '@/components/FileUpload';
import LocationFields from '@/components/profile/LocationFields';
import { SECTORS } from '@/lib/sectors';
import { useApi } from '@/hooks/use-api';

/* ── Choice building blocks — less typing, more taps ── */
export const Chips = ({ options, value, onChange, multi = false, testid }) => (
  <TooltipProvider delayDuration={150}>
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const val = typeof o === 'object' ? o.value : o;
        const label = typeof o === 'object' ? o.label : o;
        const hint = typeof o === 'object' ? o.hint : null;
        const on = multi ? (value || []).includes(val) : value === val;
        const btn = (
          <button key={val} type="button" data-testid={`${testid}-${String(val).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
            onClick={() => onChange(multi ? (on ? value.filter((x) => x !== val) : [...(value || []), val]) : val)}
            className={`rounded-full px-3.5 py-2 text-xs font-medium border transition-colors ${on ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:text-foreground bg-card'}`}>
            {label}
          </button>
        );
        if (!hint) return btn;
        return (
          <Tooltip key={val}>
            <TooltipTrigger asChild>{btn}</TooltipTrigger>
            <TooltipContent side="top" className="text-xs">{hint}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  </TooltipProvider>
);

const SliderRow = ({ label, value, onChange, min = 0, max = 10, suffix = '', testid }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <Label className="text-xs">{label}</Label>
      <span className="font-display text-lg text-teal tabular-nums" data-testid={`${testid}-value`}>{value}{suffix}</span>
    </div>
    <Slider value={[value]} min={min} max={max} step={1} onValueChange={([v]) => onChange(v)} data-testid={testid} />
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-xs">{label}{hint && <span className="text-muted-foreground font-normal"> — {hint}</span>}</Label>
    <div className="mt-2">{children}</div>
  </div>
);

export { SECTORS };
export const BUSINESS_MODELS = ['SaaS', 'Marketplace', 'Platform', 'E-commerce', 'Aggregator', 'On-Demand', 'Services', 'Productized Services', 'Rental/Sharing', 'Franchise', 'Licensing', 'Hardware + Software', 'API-as-a-Service', 'Data-as-a-Service', 'Infrastructure-as-a-Service'];
export const CUSTOMER_MODELS = [
  { value: 'B2C', label: 'B2C', hint: 'Business to Consumer' },
  { value: 'B2B', label: 'B2B', hint: 'Business to Business' },
  { value: 'B2B2C', label: 'B2B2C', hint: 'Business to Business to Consumer' },
  { value: 'P2P/C2C', label: 'P2P/C2C', hint: 'Peer to Peer / Consumer to Consumer' },
  { value: 'B2G', label: 'B2G', hint: 'Business to Government' },
  { value: 'D2C', label: 'D2C', hint: 'Direct to Consumer' },
];
export const REVENUE_MODELS = ['Subscription', 'Transaction Fee', 'Commission', 'Advertising', 'Freemium', 'Licensing/Royalties', 'Usage-Based', 'Pay-per-Use', 'Markup/Margin', 'Lead Generation', 'Affiliate', 'Revenue Share', 'Membership', 'Rental Fees', 'Service Fees', 'Interest/Spread', 'SaaS Seat-Based', 'Data Monetization', 'Other'];
export const WANTS = ['Mentorship & Strategic Guidance', 'Fundraising & Funding Readiness', 'Investor Introductions', 'Talent & Hiring Support', 'POC / Pilot Opportunities', 'Legal, Business & Finance Support', 'Ecosystem & Customer Introductions', 'Go-to-Market & Growth Support', 'Product & Technology Support', 'Other — Please specify'];
const STRENGTHS = ['Engineering', 'Product', 'Design', 'Sales', 'Marketing', 'Ops', 'Finance'];

const DEFAULTS = {
  founder: { role: '', commitment: '', years_building: 1, prior_startups: '', country: 'India', state: '', city: '' },
  venture: { venture_name: '', sectors: [], stage: '', website: '', tagline: '' },
  problem: { statement: '', who: [], severity: 7, frequency: '' },
  solution: { product_status: '', differentiation: [], demo_url: '', oneliner: '' },
  traction: { users_range: '', revenue_range: '', growth_trend: '', pilots: 0 },
  market: { tam: '', geographies: [], competition: 5, business_model: [], customer_model: [], revenue_model: [], revenue_model_other: '' },
  team: { team_size: 2, cofounders: '', strengths: [], gaps: [] },
  funding_history: { raised_before: '', amount_range: '', sources: [], runway: '' },
  funding_need: { seeking: '', use_of_funds: [], timeline: '' },
  why_bower: { wants: [], wants_other: '', expectations: '' },
  goals: { goal_12m: '', north_star: '' },
};

const STEPS = [
  { key: 'founder', title: 'You, the founder' },
  { key: 'venture', title: 'Your venture' },
  { key: 'problem', title: 'The problem' },
  { key: 'solution', title: 'Solution & product' },
  { key: 'traction', title: 'Traction' },
  { key: 'market', title: 'Market' },
  { key: 'team', title: 'Team' },
  { key: 'funding_history', title: 'Funding history' },
  { key: 'funding_need', title: 'Funding requirement' },
  { key: 'why_bower', title: 'Why Bower Capital' },
  { key: 'goals', title: 'Founder goals' },
  { key: 'documents', title: 'Documents' },
];

/* Everything is mandatory except Website, Demo link and Financials doc. */
const REQUIRED = {
  founder: [['role', 'Your role'], ['commitment', 'Commitment'], ['prior_startups', 'Startups before this one'], ['state', 'State'], ['city', 'City']],
  venture: [['venture_name', 'Venture name'], ['sectors', 'Sectors'], ['stage', 'Stage'], ['tagline', 'One-line pitch']],
  problem: [['statement', 'Problem statement'], ['who', 'Who has this problem'], ['frequency', 'Problem frequency']],
  solution: [['product_status', 'Product status'], ['differentiation', 'Why you win'], ['oneliner', 'One-line description']],
  traction: [['users_range', 'Users today'], ['revenue_range', 'Monthly revenue'], ['growth_trend', 'Growth trend']],
  market: [['tam', 'Market size'], ['geographies', 'Geographies'], ['business_model', 'Business model'], ['customer_model', 'Customer / market model'], ['revenue_model', 'Revenue model']],
  team: [['cofounders', 'Cofounders'], ['strengths', 'Team strengths'], ['gaps', 'Hiring gaps']],
  funding_history: [['raised_before', 'Raised money before'], ['runway', 'Current runway']],
  funding_need: [['seeking', 'Raise amount'], ['use_of_funds', 'Use of funds'], ['timeline', 'Raise timeline']],
  why_bower: [['wants', 'What you want from Bower Capital'], ['expectations', 'What makes it a win']],
  goals: [['goal_12m', '12-month goal'], ['north_star', 'North-star metric']],
};

export const missingFor = (key, s) => {
  const sec = s[key] || {};
  const out = (REQUIRED[key] || [])
    .filter(([k]) => (Array.isArray(sec[k]) ? sec[k].length === 0 : !String(sec[k] ?? '').trim()))
    .map(([, label]) => label);
  if (key === 'funding_history' && sec.raised_before === 'yes') {
    if (!String(sec.amount_range || '').trim()) out.push('Amount raised');
    if (!(sec.sources || []).length) out.push('Funding sources');
  }
  if (key === 'market' && (sec.revenue_model || []).includes('Other') && !String(sec.revenue_model_other || '').trim()) out.push('Revenue model — specify Other');
  if (key === 'why_bower' && (sec.wants || []).some((w) => String(w).includes('Other')) && !String(sec.wants_other || '').trim()) out.push('Please specify Other');
  return out;
};

export default function CapitalApply() {
  const api = useApi();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [sections, setSections] = useState(DEFAULTS);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    api.get('/api/capital/application').then((d) => {
      if (!d.program || d.program.status !== 'accepting') { setLocked(true); return; }
      const app = d.application;
      if (app && app.status !== 'draft') { nav('/app/capital/application', { replace: true }); return; }
      const merged = { ...DEFAULTS };
      Object.entries(app?.sections || {}).forEach(([k, v]) => { if (merged[k]) merged[k] = { ...merged[k], ...v }; });
      if (!app?.sections?.venture && d.prefill?.venture_name) {
        merged.venture = { ...merged.venture, venture_name: d.prefill.venture_name, sectors: d.prefill.sectors || [], stage: d.prefill.stage || '', website: d.prefill.website || '', tagline: d.prefill.tagline || '' };
      }
      if (!app?.sections?.founder) {
        merged.founder = { ...merged.founder, city: d.prefill?.city || '', state: d.prefill?.state || '', country: d.prefill?.country || 'India' };
      }
      setSections(merged);
      setDocuments(app?.documents || []);
      loadedRef.current = true;
    }).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (silent = true) => {
    try {
      await api.put('/api/capital/application', { sections, documents });
      if (!silent) toast.success('Draft saved — resume anytime');
    } catch (e) { if (!silent) toast.error(e.message); }
  };

  const go = async (next) => {
    if (next > step) {
      for (let i = step; i < next; i++) {
        const key = STEPS[i].key;
        if (key === 'documents') continue;
        const miss = missingFor(key, sections);
        if (miss.length) {
          toast.error('A few required answers are missing', { description: miss.join(' · ') });
          if (i !== step) { setStep(i); window.scrollTo({ top: 0 }); }
          return;
        }
      }
    }
    setSaving(true);
    await save(true);
    setSaving(false);
    setStep(next);
    window.scrollTo({ top: 0 });
  };

  const submit = async () => {
    for (let i = 0; i < STEPS.length; i++) {
      const key = STEPS[i].key;
      if (key === 'documents') continue;
      const miss = missingFor(key, sections);
      if (miss.length) {
        setStep(i);
        window.scrollTo({ top: 0 });
        toast.error(`"${STEPS[i].title}" has required answers missing`, { description: miss.join(' · ') });
        return;
      }
    }
    setSaving(true);
    try {
      await api.put('/api/capital/application', { sections, documents });
      await api.post('/api/capital/application/submit');
      toast.success('Application submitted 🎉', { description: 'Track screening, interviews and the decision on your tracker.' });
      nav('/app/capital/application');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const set = (sec) => (k, v) => setSections((s) => ({ ...s, [sec]: { ...s[sec], [k]: v } }));

  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (locked) return <div className="p-10 text-center text-muted-foreground" data-testid="capital-apply-closed">Applications are closed right now.</div>;

  const s = sections;
  const cur = STEPS[step];
  const raisedYes = s.funding_history.raised_before === 'yes';
  const hasDeck = documents.some((d) => d.kind === 'deck');

  const body = {
    founder: (
      <div className="grid gap-5">
        <Field label="Your role"><Chips options={['CEO', 'CTO', 'CPO', 'COO', 'Solo founder']} value={s.founder.role} onChange={(v) => set('founder')('role', v)} testid="apply-founder-role" /></Field>
        <Field label="Commitment"><Chips options={[{ value: 'full_time', label: 'Full-time on this' }, { value: 'part_time', label: 'Part-time for now' }, { value: 'exploring', label: 'Still exploring' }]} value={s.founder.commitment} onChange={(v) => set('founder')('commitment', v)} testid="apply-founder-commitment" /></Field>
        <SliderRow label="Years building things" value={s.founder.years_building} onChange={(v) => set('founder')('years_building', v)} max={10} suffix=" yr" testid="apply-founder-years" />
        <Field label="Startups before this one"><Chips options={['0', '1', '2', '3+']} value={s.founder.prior_startups} onChange={(v) => set('founder')('prior_startups', v)} testid="apply-founder-prior" /></Field>
        <Field label="Where are you based?">
          <LocationFields
            value={{ country: s.founder.country || 'India', state: s.founder.state || '', city: s.founder.city || '' }}
            onChange={(loc) => setSections((prev) => ({ ...prev, founder: { ...prev.founder, ...loc } }))}
            testidPrefix="apply-founder-loc" />
        </Field>
      </div>
    ),
    venture: (
      <div className="grid gap-5">
        <Field label="Venture name"><Input value={s.venture.venture_name} onChange={(e) => set('venture')('venture_name', e.target.value)} placeholder="LensLoop" data-testid="apply-venture-name" /></Field>
        <Field label="Sectors" hint="pick all that apply"><Chips options={SECTORS} value={s.venture.sectors} onChange={(v) => set('venture')('sectors', v)} multi testid="apply-venture-sectors" /></Field>
        <Field label="Stage"><Chips options={['Idea', 'Validation', 'MVP', 'Launched', 'Revenue']} value={s.venture.stage} onChange={(v) => set('venture')('stage', v)} testid="apply-venture-stage" /></Field>
        <Field label="One-line pitch"><Input value={s.venture.tagline} onChange={(e) => set('venture')('tagline', e.target.value)} placeholder="Try-at-home AR eyewear for tier-2 India" data-testid="apply-venture-tagline" /></Field>
        <Field label="Website" hint="optional"><Input value={s.venture.website} onChange={(e) => set('venture')('website', e.target.value)} placeholder="https://…" data-testid="apply-venture-website" /></Field>
      </div>
    ),
    problem: (
      <div className="grid gap-5">
        <Field label="The problem in 1–2 sentences"><Textarea rows={3} value={s.problem.statement} onChange={(e) => set('problem')('statement', e.target.value)} placeholder="Who hurts, how badly, and why now?" data-testid="apply-problem-statement" /></Field>
        <Field label="Who has this problem?"><Chips options={['Consumers', 'SMBs', 'Enterprise', 'Students', 'Government']} value={s.problem.who} onChange={(v) => set('problem')('who', v)} multi testid="apply-problem-who" /></Field>
        <SliderRow label="How painful is it? (1 = mild, 10 = hair on fire)" value={s.problem.severity} onChange={(v) => set('problem')('severity', v)} min={1} testid="apply-problem-severity" />
        <Field label="How often does it occur?"><Chips options={['daily', 'weekly', 'monthly', 'occasional']} value={s.problem.frequency} onChange={(v) => set('problem')('frequency', v)} testid="apply-problem-frequency" /></Field>
      </div>
    ),
    solution: (
      <div className="grid gap-5">
        <Field label="Product status"><Chips options={[{ value: 'concept', label: 'Concept' }, { value: 'prototype', label: 'Prototype' }, { value: 'beta', label: 'Beta users' }, { value: 'live', label: 'Live in market' }]} value={s.solution.product_status} onChange={(v) => set('solution')('product_status', v)} testid="apply-solution-status" /></Field>
        <Field label="Why you win" hint="pick your edges"><Chips options={['Speed to value', 'Price', 'Distribution', 'Tech depth', 'UX', 'Community']} value={s.solution.differentiation} onChange={(v) => set('solution')('differentiation', v)} multi testid="apply-solution-diff" /></Field>
        <Field label="What it does, in one line"><Input value={s.solution.oneliner} onChange={(e) => set('solution')('oneliner', e.target.value)} placeholder="We do X for Y so they can Z" data-testid="apply-solution-oneliner" /></Field>
        <Field label="Demo link" hint="optional — video or product URL"><Input value={s.solution.demo_url} onChange={(e) => set('solution')('demo_url', e.target.value)} placeholder="https://…" data-testid="apply-solution-demo" /></Field>
      </div>
    ),
    traction: (
      <div className="grid gap-5">
        <Field label="Users today"><Chips options={['0', '1-100', '100-1k', '1k-10k', '10k+']} value={s.traction.users_range} onChange={(v) => set('traction')('users_range', v)} testid="apply-traction-users" /></Field>
        <Field label="Monthly revenue"><Chips options={[{ value: '0', label: '₹0' }, { value: '<1L', label: 'Under ₹1L' }, { value: '1-10L', label: '₹1–10L' }, { value: '10L-1Cr', label: '₹10L–1Cr' }, { value: '1Cr+', label: '₹1Cr+' }]} value={s.traction.revenue_range} onChange={(v) => set('traction')('revenue_range', v)} testid="apply-traction-revenue" /></Field>
        <Field label="Growth trend"><Chips options={[{ value: 'up', label: 'Growing month-on-month' }, { value: 'flat', label: 'Flat' }, { value: 'early', label: 'Too early to say' }]} value={s.traction.growth_trend} onChange={(v) => set('traction')('growth_trend', v)} testid="apply-traction-trend" /></Field>
        <SliderRow label="Pilots / LOIs signed" value={s.traction.pilots} onChange={(v) => set('traction')('pilots', v)} testid="apply-traction-pilots" />
      </div>
    ),
    market: (
      <div className="grid gap-5">
        <Field label="Market size (TAM)"><Chips options={[{ value: '<100Cr', label: 'Under ₹100 Cr' }, { value: '100-1kCr', label: '₹100–1,000 Cr' }, { value: '1k-10kCr', label: '₹1k–10k Cr' }, { value: '10kCr+', label: '₹10k Cr+' }]} value={s.market.tam} onChange={(v) => set('market')('tam', v)} testid="apply-market-tam" /></Field>
        <Field label="Geographies"><Chips options={['India', 'SEA', 'MENA', 'US / EU', 'Global']} value={s.market.geographies} onChange={(v) => set('market')('geographies', v)} multi testid="apply-market-geo" /></Field>
        <SliderRow label="Competition intensity (1 = open field, 10 = red ocean)" value={s.market.competition} onChange={(v) => set('market')('competition', v)} min={1} testid="apply-market-competition" />
        <Field label="Business model" hint="how you create & deliver value — pick all that apply"><Chips options={BUSINESS_MODELS} value={s.market.business_model} onChange={(v) => set('market')('business_model', v)} multi testid="apply-market-model" /></Field>
        <Field label="Customer / market model" hint="hover for full forms"><Chips options={CUSTOMER_MODELS} value={s.market.customer_model} onChange={(v) => set('market')('customer_model', v)} multi testid="apply-market-customer-model" /></Field>
        <Field label="Revenue model" hint="how you make money — pick all that apply"><Chips options={REVENUE_MODELS} value={s.market.revenue_model} onChange={(v) => set('market')('revenue_model', v)} multi testid="apply-market-revenue-model" /></Field>
        {(s.market.revenue_model || []).includes('Other') && (
          <Field label="Revenue model — Other"><Input value={s.market.revenue_model_other} onChange={(e) => set('market')('revenue_model_other', e.target.value)} placeholder="Tell us how you make money" data-testid="apply-market-revenue-other" /></Field>
        )}
      </div>
    ),
    team: (
      <div className="grid gap-5">
        <SliderRow label="Team size (including you)" value={s.team.team_size} onChange={(v) => set('team')('team_size', v)} min={1} max={15} testid="apply-team-size" />
        <Field label="Cofounders"><Chips options={['0', '1', '2', '3+']} value={s.team.cofounders} onChange={(v) => set('team')('cofounders', v)} testid="apply-team-cofounders" /></Field>
        <Field label="Team strengths"><Chips options={STRENGTHS} value={s.team.strengths} onChange={(v) => set('team')('strengths', v)} multi testid="apply-team-strengths" /></Field>
        <Field label="Gaps you want to hire for"><Chips options={STRENGTHS} value={s.team.gaps} onChange={(v) => set('team')('gaps', v)} multi testid="apply-team-gaps" /></Field>
      </div>
    ),
    funding_history: (
      <div className="grid gap-5">
        <Field label="Raised money before?"><Chips options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'Not yet' }]} value={s.funding_history.raised_before} onChange={(v) => set('funding_history')('raised_before', v)} testid="apply-fh-raised" /></Field>
        {raisedYes && (
          <>
            <Field label="How much in total?"><Chips options={[{ value: '<10L', label: 'Under ₹10L' }, { value: '10-50L', label: '₹10–50L' }, { value: '50L-2Cr', label: '₹50L–2Cr' }, { value: '2Cr+', label: '₹2Cr+' }]} value={s.funding_history.amount_range} onChange={(v) => set('funding_history')('amount_range', v)} testid="apply-fh-amount" /></Field>
            <Field label="From whom?"><Chips options={['Friends & family', 'Angels', 'VCs', 'Grants', 'Accelerator']} value={s.funding_history.sources} onChange={(v) => set('funding_history')('sources', v)} multi testid="apply-fh-sources" /></Field>
          </>
        )}
        <Field label="Current runway"><Chips options={[{ value: '<3m', label: 'Under 3 months' }, { value: '3-6m', label: '3–6 months' }, { value: '6-12m', label: '6–12 months' }, { value: '12m+', label: '12+ months' }, { value: 'bootstrapped', label: 'Bootstrapped / no burn' }]} value={s.funding_history.runway} onChange={(v) => set('funding_history')('runway', v)} testid="apply-fh-runway" /></Field>
      </div>
    ),
    funding_need: (
      <div className="grid gap-5">
        <Field label="How much are you looking to raise?"><Chips options={[{ value: '10-25L', label: '₹10–25L' }, { value: '25-50L', label: '₹25–50L' }, { value: '50L-1Cr', label: '₹50L–1Cr' }, { value: '1Cr+', label: '₹1Cr+' }]} value={s.funding_need.seeking} onChange={(v) => set('funding_need')('seeking', v)} testid="apply-fn-seeking" /></Field>
        <Field label="Use of funds"><Chips options={['Product', 'Hiring', 'Marketing', 'Ops', 'Inventory']} value={s.funding_need.use_of_funds} onChange={(v) => set('funding_need')('use_of_funds', v)} multi testid="apply-fn-use" /></Field>
        <Field label="When do you need it?"><Chips options={[{ value: 'asap', label: 'ASAP' }, { value: '3-6 months', label: 'In 3–6 months' }, { value: '6-12 months', label: 'In 6–12 months' }, { value: 'exploring', label: 'Just exploring' }]} value={s.funding_need.timeline} onChange={(v) => set('funding_need')('timeline', v)} testid="apply-fn-timeline" /></Field>
        <p className="text-[11px] text-muted-foreground">Funding support is optional and evaluated separately — you can be selected for the program without raising.</p>
      </div>
    ),
    why_bower: (
      <div className="grid gap-5">
        <Field label="What do you want most from Bower Capital?" hint="pick all that apply"><Chips options={WANTS} value={s.why_bower.wants} onChange={(v) => set('why_bower')('wants', v)} multi testid="apply-why-wants" /></Field>
        {(s.why_bower.wants || []).some((w) => String(w).includes('Other')) && (
          <Field label="Other — please specify"><Input value={s.why_bower.wants_other} onChange={(e) => set('why_bower')('wants_other', e.target.value)} placeholder="Tell us what you need most" data-testid="apply-why-wants-other" /></Field>
        )}
        <Field label="What would make the 12 weeks a win for you?"><Textarea rows={3} value={s.why_bower.expectations} onChange={(e) => set('why_bower')('expectations', e.target.value)} placeholder="Be specific — we hold ourselves to it." data-testid="apply-why-expectations" /></Field>
      </div>
    ),
    goals: (
      <div className="grid gap-5">
        <Field label="Your #1 goal for the next 12 months"><Chips options={['First revenue', '₹10L MRR', 'Raise a round', 'Product launch', 'Team of 10', '10k users']} value={s.goals.goal_12m} onChange={(v) => set('goals')('goal_12m', v)} testid="apply-goals-12m" /></Field>
        <Field label="Your north-star metric"><Input value={s.goals.north_star} onChange={(e) => set('goals')('north_star', e.target.value)} placeholder="e.g. weekly active teams" data-testid="apply-goals-northstar" /></Field>
      </div>
    ),
    documents: (
      <div className="grid gap-5">
        <div className="rounded-xl border border-border bg-muted/40 p-4">
          <p className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-teal" /> Pitch deck <span className="text-red-500">*</span></p>
          <p className="text-xs text-muted-foreground mt-1 mb-3">PDF preferred. Stored privately — only the Bower Capital team sees your files.</p>
          <FileUpload folder="capital/applications" accept=".pdf,.ppt,.pptx,.key" label="Upload pitch deck" resetAfterUpload testid="apply-doc-deck"
            onUploaded={({ key, filename }) => setDocuments((d) => [...d.filter((x) => x.kind !== 'deck'), { key, name: filename, kind: 'deck' }])} />
        </div>
        <div className="rounded-xl border border-border bg-muted/40 p-4">
          <p className="text-sm font-medium">Financials / traction sheet <span className="text-muted-foreground font-normal">(optional)</span></p>
          <div className="mt-3">
            <FileUpload folder="capital/applications" accept=".pdf,.xls,.xlsx,.csv" label="Upload financials" resetAfterUpload testid="apply-doc-financials"
              onUploaded={({ key, filename }) => setDocuments((d) => [...d, { key, name: filename, kind: 'financials' }])} />
          </div>
        </div>
        {documents.length > 0 && (
          <div data-testid="apply-doc-list">
            <Label className="text-xs">Attached</Label>
            <ul className="mt-2 space-y-1.5">
              {documents.map((d, i) => (
                <li key={`${d.key}-${i}`} className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2">
                  <span className="text-sm truncate flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-teal shrink-0" /> {d.name} <Badge variant="outline" className="text-[9px] border-border">{d.kind}</Badge></span>
                  <button className="text-xs text-muted-foreground hover:text-destructive" onClick={() => setDocuments((docs) => docs.filter((_, j) => j !== i))} data-testid={`apply-doc-remove-${i}`}>Remove</button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {!hasDeck && <p className="text-xs text-gold">A pitch deck is required to submit.</p>}
      </div>
    ),
  }[cur.key];

  return (
    <div className="px-4 sm:px-5 lg:px-8 py-6 lg:py-8 max-w-3xl mx-auto" data-testid="capital-apply-page">
      <Link to="/app/capital" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5"><ArrowLeft className="h-4 w-4" /> Bower Capital</Link>
      <div className="flex items-center justify-between mb-1">
        <p className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Application · step {step + 1} of {STEPS.length}</p>
        <button className="text-xs text-teal font-medium" onClick={() => save(false)} data-testid="apply-save-draft-btn">Save draft</button>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-5">{cur.title}</h1>
      <div className="flex gap-1 mb-7" data-testid="apply-progress">
        {STEPS.map((st, i) => (
          <button key={st.key} onClick={() => go(i)} title={st.title}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i < step ? 'bg-teal' : i === step ? 'bg-foreground' : 'bg-border'}`}
            data-testid={`apply-step-dot-${st.key}`} />
        ))}
      </div>

      <div className="rounded-3xl border border-border bg-card p-5 sm:p-7">{body}</div>

      <div className="flex items-center justify-between mt-6">
        <Button variant="outline" className="rounded-full gap-2" disabled={step === 0 || saving} onClick={() => go(step - 1)} data-testid="apply-back-btn"><ArrowLeft className="h-4 w-4" /> Back</Button>
        {step < STEPS.length - 1 ? (
          <Button className="rounded-full gap-2 bg-foreground text-background hover:bg-foreground/90" disabled={saving} onClick={() => go(step + 1)} data-testid="apply-next-btn">Next <ArrowRight className="h-4 w-4" /></Button>
        ) : (
          <Button className="rounded-full gap-2 bg-teal text-white hover:bg-teal/90" disabled={saving || !hasDeck} onClick={submit} data-testid="apply-submit-btn"><Check className="h-4 w-4" /> {saving ? 'Submitting…' : 'Submit application'}</Button>
        )}
      </div>
    </div>
  );
}
